import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import * as amplitude from "@amplitude/analytics-browser";

import type { IButton } from "libs/design-system/src/types/Button/button";
import { getValueFromNestedObject } from "libs/design-system/src/utils/getValueFromNestedObject";
import { Check, Xmark } from "@unified-checkout/design-system/icons/index";
import {
  Button,
  StatusIndicator,
  Modal,
  deepObjectsCompare
} from "@unified-checkout/design-system";

import {
  additionalValuesFields,
  CHECKOUT_CUSTOMIZATION_EVENTS,
  CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS,
  classifiedTrackableFieldsNames,
  trackFieldsUpdate
} from "@app/dashboard/constants/tracking_events/checkout-customization";
import {
  SHARED_EVENTS_PARAMS,
  SHARED_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/shared";
import { SYSTEM_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/system";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import {
  INITIAL_STATE as CUSTOMIZATION_INITIAL_STATE,
  useCheckoutCustomizationStore
} from "@app/dashboard/store/checkout-customization";
import { resetCustomizationData } from "@app/dashboard/services/inner_layout/checkout_customization/reset-data";
import { updateCustomizationData } from "@app/dashboard/services/inner_layout/checkout_customization/update-data";
import { updateCustomizationLogo } from "@app/dashboard/services/inner_layout/checkout_customization/update-logo";
import type {
  IInfoModal,
  TActionModal,
  TInfoModal,
  TNavTabs
} from "@app/dashboard/types/innerLayout/checkout_customization";
import type { TModalProps } from "@app/dashboard/types/innerLayout/checkout_customization/payment-methods";
import {
  CUSTOMIZATION_SECTIONS_KEYS,
  filterToMatchStructure,
  INFO_MODAL_WIDTH
} from "./utils";
import styles from "./checkout-customization.module.scss";


const ACTION_BTN_SIZE = "xl";

const { UPDATE, RESET } = {
  UPDATE: "update" as const,
  RESET: "reset" as const
};

const { SUCCESS, FAILURE } = {
  SUCCESS: "success" as const,
  FAILURE: "failure" as const
};

const { BUSINESS_LOGO } = CUSTOMIZATION_SECTIONS_KEYS;

export const ActionsBox = ({
  activeTab,
}: {
  activeTab: TNavTabs,
}) => {
  const { t } = useTranslation();

  const {
    isFetched,
    isLoading,
    errors,
    setErrors,
    initialData,
    setInitialData,
    customizationData,
    businessLogo,
    setBusinessLogo,
    setCustomizationData,
    setIsDataReset
  } = useCheckoutCustomizationStore();

  const [isClean, setIsClean] = useState(true);
  const [infoModalProps, setInfoModalProps] = useState<IInfoModal>({
    open: false,
    text: '',
  });
  const [actionModalProps, setActionModalProps] = useState<TModalProps & { type?: TActionModal }>({
    open: false,
    text: '',
    hint: '',
  });

  const handleFormError = (fieldName: string, err: AxiosError) => {
    const axiosErr = err as AxiosError;
    const response = axiosErr?.response as any;
    const msg = response?.data?.message;
    const fieldMsg = response?.data?.[BUSINESS_LOGO]?.[0];

    setErrors(fieldName, fieldMsg || msg || t('innerLayout.errors.smthWentWrong'));
  };

  const {
    isPending: isUpdateLogoPending,
    mutate: mutateUpdateLogo,
  } = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_BUSINESS_LOGO],
    mutationFn: (file?: File | null) => updateCustomizationLogo({ merchantLogo: file }),
    onSuccess: (response) => {
      const responseData = response?.data;
      const { merchant_logo } = responseData;

      setInitialData({
        ...initialData,
        merchant_logo
      });

      setBusinessLogo({
        ...businessLogo,
        path: merchant_logo
      });

    },
    onError: (err) => {
      handleFormError(BUSINESS_LOGO, err as AxiosError);
    }
  });

  const {
    isPending: isResetLogoPending,
    mutate: mutateResetLogo,
  } = useMutation({
    mutationKey: [QUERY_KEYS.RESET_BUSINESS_LOGO],
    mutationFn: () => updateCustomizationLogo({ reset: true }),
    onSuccess: (response) => {

      setInitialData({
        ...initialData,
        merchant_logo: null
      });

      setBusinessLogo({
        path: null,
        file: null
      });

    },
    onError: (err) => {
      handleFormError(BUSINESS_LOGO, err as AxiosError);
    }
  });

  const {
    isPending: isResetPending,
    mutate: mutateReset,
  } = useMutation({
    mutationKey: [QUERY_KEYS.RESET_CUSTOMIZATION_DATA],
    mutationFn: () => resetCustomizationData(),
    onSuccess: (response) => {
      const responseData = response?.data;
      const { customization_data } = responseData;

      const resetData = filterToMatchStructure(CUSTOMIZATION_INITIAL_STATE, customization_data);

      setCustomizationData(resetData);
      setInitialData({
        ...initialData,
        customization_data: resetData
      });

      setIsDataReset();

      setActionModalProps(prev => ({
        ...prev,
        open: false
      }));

      setInfoModalProps({
        open: true,
        type: SUCCESS,
        text: infoModalsDetails[SUCCESS][RESET]
      });

      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.RESET_CONFIRMED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.ACTIVE_TAB]: activeTab
      });
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message;

      setActionModalProps(prev => ({
        ...prev,
        open: false
      }));

      setInfoModalProps({
        open: true,
        type: FAILURE,
        text: msg || infoModalsDetails[FAILURE][RESET]
      });
    }
  });

  const {
    isPending: isUpdatePending,
    mutate: mutateUpdate,
  } = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_CUSTOMIZATION_DATA],
    mutationFn: () => updateCustomizationData({ data: { ...customizationData, isDefault: false } }),
    onSuccess: (response) => {
      const responseData = response?.data;
      const { customization_data } = responseData;

      const afterSubmissionData = filterToMatchStructure(CUSTOMIZATION_INITIAL_STATE, customization_data);

      setCustomizationData(afterSubmissionData);
      setInitialData({
        ...initialData,
        customization_data: afterSubmissionData
      });

      setActionModalProps(prev => ({
        ...prev,
        open: false
      }));

      setInfoModalProps({
        open: true,
        type: SUCCESS,
        text: infoModalsDetails[SUCCESS][UPDATE]
      });

      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.SAVE_CONFIRMED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.ACTIVE_TAB]: activeTab
      });
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const serverErrMsg = response?.data?.message;
      const errMsg = serverErrMsg || infoModalsDetails[FAILURE][UPDATE];

      setActionModalProps(prev => ({
        ...prev,
        open: false
      }));

      setInfoModalProps({
        open: true,
        type: FAILURE,
        text: errMsg
      });

      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.SAVE_FAILED, {
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: errMsg
      });
    }
  });

  const memoizedInitialData = useMemo(() => initialData, [initialData]);
  const memoizedInitialDataCompare = useMemo(() => ({
    customization_data: customizationData, merchant_logo: businessLogo.path
  }), [customizationData, businessLogo.path]);

  useEffect(() => {
    if (initialData === undefined) {
      setIsClean(true);
    } else {
      const isIdentical = deepObjectsCompare(
        memoizedInitialData,
        memoizedInitialDataCompare,
        {
          ignoreKeys: ['rank', 'group_rank']
        }
      );
      setIsClean(isIdentical);
    }
  }, [memoizedInitialData, memoizedInitialDataCompare]);

  useEffect(() => {
    if (actionModalProps.open) {
      const modalType = actionModalProps.type as TActionModal;
      setActionModalProps(prev => ({
        ...prev,
        btnsProps: actionModalsDetails[modalType].btnsProps
      }));
    }
  }, [isUpdatePending, isResetPending, actionModalProps.open]);

  useEffect(() => {
    infoModalProps.open &&
      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.CHANGES_APPLIED_TOAST_VIEWED, {
        [SYSTEM_EVENTS_PARAMS.TOAST_TYPE]:
          infoModalProps.type === "success" ?
            SHARED_EVENTS_VALUES.SUCCESS
            :
            SHARED_EVENTS_VALUES.FAILURE,
        [SYSTEM_EVENTS_PARAMS.MESSAGE]: infoModalProps.text
      });
  }, [infoModalProps]);


  const trackChangedFields = async () => {
    const fieldsToCompare = classifiedTrackableFieldsNames;

    for (const sectionKey in fieldsToCompare) {
      const fields = fieldsToCompare[sectionKey as keyof typeof fieldsToCompare];

      for (const field of fields) {
        const initialValue = getValueFromNestedObject(initialData, `customization_data.${sectionKey}.${field}`);
        const currentValue = getValueFromNestedObject(customizationData, `${sectionKey}.${field}`);

        if (!initialValue && !currentValue) continue;

        if (typeof initialValue === "object" && typeof currentValue === "object") {
          JSON.stringify(initialValue) !== JSON.stringify(currentValue) &&
            trackFieldsUpdate(sectionKey as TNavTabs, field, currentValue);

        } else if (initialValue !== currentValue) {
          const additionalValue = getValueFromNestedObject(customizationData, additionalValuesFields[field] || '');

          trackFieldsUpdate(sectionKey as TNavTabs, field, currentValue, additionalValue);
        }
      }
    }
  };

  const handleModalCancel = () => setActionModalProps(prev => ({ ...prev, open: false }));

  const handleModalConfirm = async (type: TActionModal) => {
    if (type === RESET) {
      mutateReset();
      mutateResetLogo();

      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.RESET_BTN_CLICKED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.ACTIVE_TAB]: activeTab
      });
    } else {
      await trackChangedFields();

      mutateUpdate();

      const isLogoChanged = businessLogo.path !== initialData?.merchant_logo;

      isLogoChanged && !businessLogo.file && mutateUpdateLogo(businessLogo.file);

      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.APPLY_CHANGES_CLICKED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.ACTIVE_TAB]: activeTab
      });
    }
  };

  const handleCustomizationReset = () => {
    setActionModalProps({
      open: true,
      ...actionModalsDetails[RESET]
    });
  };

  const handleCustomizationUpdate = () => {
    setActionModalProps({
      open: true,
      ...actionModalsDetails[UPDATE]
    });
  };


  const modalCancelBtnProps: IButton = {
    color: "secondary",
    variant: "outlined",
    text: t('form.cancel'),
    onClickHandler: () => handleModalCancel(),
    disabled: isResetPending || isUpdatePending
  };

  const actionModalsDetails: Record<TActionModal, Omit<TModalProps, 'open'>> = useMemo(() => ({
    [UPDATE]: {
      type: UPDATE,
      text: t('innerLayout.checkoutCustomizationPage.modals.update.title'),
      hint: t('innerLayout.checkoutCustomizationPage.modals.update.hint'),
      btnsProps: [
        {
          text: t('form.confirm'),
          onClickHandler: () => handleModalConfirm(UPDATE),
          loading: isUpdatePending
        },
        modalCancelBtnProps
      ]
    },
    [RESET]: {
      type: RESET,
      text: t('innerLayout.checkoutCustomizationPage.modals.reset.title'),
      hint: t('innerLayout.checkoutCustomizationPage.modals.reset.hint'),
      btnsProps: [
        {
          text: t('form.reset'),
          onClickHandler: () => handleModalConfirm(RESET),
          loading: isResetPending
        },
        modalCancelBtnProps
      ]
    }
  }), [t, isUpdatePending, isResetPending, modalCancelBtnProps]);

  const infoModalsDetails: Record<TInfoModal, Record<TActionModal, string>> = {
    [SUCCESS]: {
      [UPDATE]: t('innerLayout.checkoutCustomizationPage.modals.success.update'),
      [RESET]: t('innerLayout.checkoutCustomizationPage.modals.success.reset')
    },
    [FAILURE]: {
      [UPDATE]: t('errors.somethingWentWrong'),
      [RESET]: t('errors.somethingWentWrong')
    },
  };


  const isApplyBtnDisabled =
    (Object.keys(errors).length > 0) ||
    isClean ||
    isLoading ||
    isResetPending ||
    isUpdatePending ||
    isResetLogoPending ||
    isUpdateLogoPending ||
    !isFetched;

  const actionBtns: IButton[] = [
    ...(customizationData.isDefault === false ? [{
      size: ACTION_BTN_SIZE,
      color: "secondary",
      variant: "outlined",
      text: t('form.reset'),
      onClickHandler: () => handleCustomizationReset()
    }] : []) as IButton[],
    {
      size: ACTION_BTN_SIZE,
      color: "primary",
      variant: "contained",
      text: t('innerLayout.checkoutCustomizationPage.preview.cta.applyChanges'),
      disabled: isApplyBtnDisabled,
      onClickHandler: () => handleCustomizationUpdate(),
    }
  ];
  return (
    <>
      {
        actionBtns.map((btnProps, index) => (
          <Button
            key={`ch-custom-action-btn-${index}`}
            {...btnProps}
          />
        ))
      }

      <Modal
        open={actionModalProps.open}
        title={actionModalProps.text}
        supportingText={actionModalProps.hint}
        btnsProps={actionModalProps.btnsProps}
        closeOnClickOutside={false}
        onCloseHandler={() => handleModalCancel()}
      />

      <Modal
        width={INFO_MODAL_WIDTH}
        open={infoModalProps.open}
        hasCloseIcon
        onCloseHandler={() => setInfoModalProps(prev => ({ ...prev, open: false }))}
        title=""
      >
        <div className={styles["checkout-customization-wrapper__info-modal-body"]}>
          <StatusIndicator
            status={infoModalProps.type === SUCCESS ? "success" : "error"}
            icon={infoModalProps.type === SUCCESS ? Check : Xmark}
            hintText={infoModalProps.text}
          />
        </div>
      </Modal>

    </>
  )
};